import { useEffect, useState } from "react";
import { History, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errorText } from "@/lib/api";
import { toast } from "sonner";

const ACTION_STYLE = {
  upload: "bg-blue-100 text-blue-800", generate: "bg-emerald-100 text-emerald-800", override: "bg-amber-100 text-amber-800",
  driver: "bg-purple-100 text-purple-800", version: "bg-slate-100 text-slate-700", approval: "bg-rose-100 text-rose-800",
  tracking: "bg-cyan-100 text-cyan-800", comment: "bg-yellow-100 text-yellow-800",
};

export const AuditTrailPanel = ({ projectId, refreshKey }) => {
  const [events, setEvents] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = () => api.get(`/projects/${projectId}/audit`).then((r) => setEvents(r.data)).catch((e) => toast.error(errorText(e)));
  useEffect(() => { load(); }, [projectId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const actions = [...new Set((events || []).map((e) => e.action))];
  const visible = (events || []).filter((e) => filter === "all" || e.action === filter);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="audit-trail-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900 inline-flex items-center gap-2"><History size={16} className="text-emerald-600" /> Audit Trail</h3>
          <p className="text-[11px] text-slate-500 mt-1">Who changed assumptions, overrides, drivers, versions, approvals and tracking — and when.</p>
        </div>
        <Button size="sm" variant="ghost" onClick={load} data-testid="audit-refresh-button"><RefreshCw size={14} /></Button>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="audit-filter">
        <button onClick={() => setFilter("all")} className={`px-2.5 py-1 rounded-full text-[11px] border ${filter === "all" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600"}`} data-testid="audit-filter-all">All ({(events || []).length})</button>
        {actions.map((a) => <button key={a} onClick={() => setFilter(a)} className={`px-2.5 py-1 rounded-full text-[11px] border capitalize ${filter === a ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600"}`} data-testid={`audit-filter-${a}`}>{a}</button>)}
      </div>
      <ul className="mt-4 max-h-[420px] overflow-auto divide-y divide-slate-100" data-testid="audit-list">
        {events === null && <li className="py-3 text-sm text-slate-400">Loading...</li>}
        {events !== null && !visible.length && <li className="py-3 text-sm text-slate-400">No activity recorded yet.</li>}
        {visible.map((e, i) => (
          <li key={i} className="py-2.5 flex items-start gap-3 text-sm" data-testid={`audit-event-${i}`}>
            <span className={`shrink-0 text-[10px] uppercase tracking-wider font-semibold rounded px-1.5 py-0.5 mt-0.5 ${ACTION_STYLE[e.action] || "bg-slate-100 text-slate-700"}`}>{e.action}</span>
            <div className="min-w-0 flex-1">
              <p className="text-slate-800 break-words">{e.details || e.action}</p>
              <p className="text-[11px] text-slate-500 font-mono mt-0.5">{e.user_name} · {new Date(e.at).toLocaleString("en-GB")}</p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
